import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  NewStore,
  ShopPage,
  SlugTaken,
  Store,
  StoreNotFound,
  StoreRoleForbidden,
  StoreUpdate,
} from "@hobbyshopify/backend-client";
import { type AsyncResult, describeErrors } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { LocationsRepository } from "../repositories/locations-repository";
import type { StoreRepository } from "../repositories/store-repository";
import type { BackendApi } from "./api";

/** Fetches the merchant's shops into the repository. Single flight. */
export async function loadStores(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeRepository: StoreRepository;
}): Promise<void> {
  const loading = () => o.storeRepository.status.kind === "loading";
  if (loading()) return;
  o.storeRepository.setLoading();
  const r = await o.authHandler.callApi((ctx) => o.client.stores.list(ctx));
  // Reset (a sign-out) while in flight: the answer belongs to a session that is gone.
  if (!loading()) return;
  if (!r.ok) {
    o.storeRepository.setError(describeErrors(r.errors));
    return;
  }
  o.storeRepository.setStores(r.value);
}

/** One shop, fresh from the server; the repository row is replaced with the answer. */
export async function loadStore(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeRepository: StoreRepository;
  storeId: string;
}): AsyncResult<Store, ApiNotAuthorized | StoreNotFound> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.get(ctx, o.storeId));
  if (r.ok) o.storeRepository.upsert(r.value);
  return r;
}

/** Creates the shop and makes it the selected one. */
export async function createStore(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeRepository: StoreRepository;
  body: NewStore;
}): AsyncResult<Store, ApiNotAuthorized | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.create(ctx, o.body));
  if (!r.ok) return r;
  o.storeRepository.upsert(r.value);
  o.storeRepository.select(r.value.id);
  return r;
}

export async function updateStore(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeRepository: StoreRepository;
  storeId: string;
  body: StoreUpdate;
}): AsyncResult<Store, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.update(ctx, o.storeId, o.body));
  if (r.ok) o.storeRepository.upsert(r.value);
  return r;
}

/**
 * Claims the hosted shop page address. The answer is the page's slug and URL,
 * not a Store, and the claim also clears `websiteUrl` server-side, so the shop
 * is fetched again rather than patched.
 */
export async function claimShopPage(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeRepository: StoreRepository;
  storeId: string;
  slug?: string;
}): AsyncResult<ShopPage, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SlugTaken | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) => o.client.stores.claimShopPage(ctx, o.storeId, o.slug));
  if (r.ok) await loadStore(o);
  return r;
}

/** Fetches the selected shop's branches and ship-from choice into the repository. Single flight per shop. */
export async function loadLocations(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  locationsRepository: LocationsRepository;
  storeId: string;
}): Promise<void> {
  const repo = o.locationsRepository;
  if (repo.storeId === o.storeId && repo.status.kind === "loading") return;
  repo.setLoading(o.storeId);
  const r = await o.authHandler.callApi((ctx) => o.client.locations.list(ctx, o.storeId));
  if (repo.storeId !== o.storeId) return;
  if (!r.ok) {
    repo.setError(o.storeId, describeErrors(r.errors));
    return;
  }
  repo.setLoaded(o.storeId, r.value);
}
